import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import type { FollowUpJobOutcome } from '@/components/organisms/estimates/support/follow-up-job-outcome';
import type { Estimate, EstimateFulfillmentMode } from '@/types/estimate';

/**
 * Where the operator goes once a Job-linked estimate's work decision has been
 * recorded (approve, fulfill, or signature + approve).
 *
 * A follow-up Job the backend did not create is the operator's to review: open
 * the New Job form prefilled from the estimate — the same screen "Convert to
 * Job" opens for an estimate that was not created on a Job — and the Job is
 * created when they save it. The backend only creates the Job itself for a
 * tenant that opted into auto-convert, and the response says so (`jobId` /
 * `CONVERTED`); the one case that stays put without a Job is an opted-in tenant
 * whose required deposit is still outstanding, where the Job is queued behind
 * the deposit.
 */
export function useFollowUpJobHandoff() {
  const navigate = useNavigate();
  return (
    mode: EstimateFulfillmentMode,
    outcome: FollowUpJobOutcome,
    estimate: Pick<Estimate, 'id' | 'status' | 'jobId'>,
  ) => {
    if (mode !== 'FOLLOW_UP_JOB') return;
    // The backend created the Job (auto-convert tenant, or a retry of an
    // estimate that had already converted) — nothing left to review.
    if (estimate.status === 'CONVERTED' || estimate.jobId) return;
    if (outcome === 'CREATED_AFTER_DEPOSIT') {
      toast.success('Follow-up Job queued until the required deposit is collected');
      return;
    }
    // REVIEW_IN_FORM — and also an opted-in tenant whose auto-convert failed
    // (it is fault-isolated server-side): either way the Job does not exist
    // yet and the operator can create it from the form.
    void navigate({ to: '/dashboard/jobs/new', search: { estimateId: estimate.id } as never });
  };
}
